import { TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { ReactNode } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmployeesGroupsByTokens } from 'src/features/employees-groups/api/hooks/use-employees-groups-by-tokens';

export interface SpaceGroupsActionsSlotPayload {
  token: string;
  name: string;
  employeeGroupsTokens: string[];
}

interface Props {
  token: string;
  name: string;
  employeeGroupsTokens: string[];
  actionsSlot: (payload: SpaceGroupsActionsSlotPayload) => ReactNode;
}

export const EmployeesGroupDetailsSpaceGroupsTableRow = ({
  token,
  name,
  employeeGroupsTokens,
  actionsSlot,
}: Props) => {
  const { t } = useTranslation(['employeesGroups', 'common']);

  const { results: employeeGroups } =
    useEmployeesGroupsByTokens(employeeGroupsTokens);

  const employeeGroupsNames =
    employeeGroups?.hits?.map(group => group.name) ?? [];

  const renderEmployeeGroupsNames = useMemo(() => {
    switch (true) {
      case employeeGroupsNames.length === 1:
        return (
          <Typography variant="body2">{employeeGroupsNames[0]}</Typography>
        );

      case employeeGroupsNames.length > 1:
        return (
          <Tooltip
            title={employeeGroupsNames.map(groupName => (
              <Typography key={groupName} variant="body2">
                {groupName}
              </Typography>
            ))}
            placement="bottom-start">
            <span>
              <Typography
                variant="body2"
                sx={{
                  textDecoration: 'underline',
                }}>{`${employeeGroupsNames.length} ${t('employeesGroups:common.table.groups')}`}</Typography>
            </span>
          </Tooltip>
        );

      default:
        return (
          <Typography variant="body2" color="textSecondary">
            {t('common:notAssigned')}
          </Typography>
        );
    }
  }, [employeeGroupsNames, t]);

  return (
    <TableRow>
      <TableCell>
        <Typography variant="inherit">{name}</Typography>
      </TableCell>

      <TableCell>{renderEmployeeGroupsNames}</TableCell>

      <TableCell align="right" sx={{ cursor: 'default' }}>
        {actionsSlot({ token, name, employeeGroupsTokens })}
      </TableCell>
    </TableRow>
  );
};
